/**
 * useSelectionValidation - Validation Logic for Selections
 *
 * Single responsibility: Validating that all required questions are answered.
 * Pure validation logic with no side effects.
 */

import { useCallback } from 'react';
import type { PickemDetailResponse } from '@low6-pickem/api-types';
import { QUESTION_TYPES } from '@/constants/question-types';
import { getQuestionId } from '@/utils/id-helpers';
import type { SelectionState } from './useSelectionState';

/**
 * Creates validation functions for selections
 *
 * @param pickemDetails - Pickem details containing questions
 * @param selections - Current selection state
 * @returns Object with validation functions
 */
export function useSelectionValidation(
  pickemDetails: PickemDetailResponse | undefined,
  selections: SelectionState
) {
  /**
   * Check if all questions have been answered
   *
   * @returns true if all required questions are answered
   */
  const allQuestionsAnswered = useCallback(() => {
    if (!pickemDetails?.pickem.questions) return false;

    // Get all required questions (exclude tiebreaker)
    const requiredQuestions = pickemDetails.pickem.questions.filter(
      q => q.question_type !== QUESTION_TYPES.TIEBREAKER
    );

    // Check if all regular questions are answered
    const allRegularQuestionsAnswered = requiredQuestions.every(question => {
      const questionId = getQuestionId(question);

      if (
        question.question_type === QUESTION_TYPES.TEAM ||
        question.question_type === QUESTION_TYPES.PLAYER
      ) {
        return !!selections.teams[questionId];
      } else if (question.question_type === QUESTION_TYPES.PROP) {
        return !!selections.props[questionId];
      }

      return true;
    });

    // Check if tiebreaker exists and is filled
    const hasTiebreaker = pickemDetails.pickem.questions.some(
      q => q.question_type === QUESTION_TYPES.TIEBREAKER
    );
    const tiebreakerFilled = hasTiebreaker ? !!selections.tieBreaker : true;

    return allRegularQuestionsAnswered && tiebreakerFilled;
  }, [pickemDetails, selections]);

  return {
    allQuestionsAnswered,
  };
}
